import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { FiCheck, FiX, FiUser, FiTarget, FiZap, FiCalendar } from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { initiateRazorpayPayment } from '../services/razorpay';

const BumpOffer = ({ customerData, onAccept, onDecline }) => {
  const [isProcessing, setIsProcessing] = useState(false);

  const handleAcceptBump = async () => {
    setIsProcessing(true);
    
    try {
      const options = {
        amount: 29900, // ₹299 in paise
        prefill: {
          name: customerData.name,
          email: customerData.email,
          contact: customerData.phone
        },
        notes: {
          product: 'diwali-ebook-bump',
          customer_email: customerData.email,
          customer_phone: customerData.phone,
          offer: '1-to-1 Expert Call'
        }
      };

      const result = await initiateRazorpayPayment(options);

      if (result.success) {
        const bumpData = {
          paymentId: result.paymentId,
          customer: customerData,
          timestamp: new Date().toISOString(),
          amount: 29900,
          product: 'diwali-ebook-bump',
          offer: '1-to-1 Expert Call'
        };

        localStorage.setItem('bumpPayment', JSON.stringify(bumpData));
        onAccept?.(bumpData);
      }
    } catch (error) {
      console.error('Bump payment error:', error);
      alert('Payment failed. Please try again or skip this offer.');
    } finally {
      setIsProcessing(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <motion.div
        className="bg-white rounded-3xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
      >
        <div className="bg-gradient-to-r from-orange-500 to-red-600 text-white p-8 rounded-t-3xl text-center">
          <div className="text-5xl mb-4">⚡</div>
          <h2 className="text-3xl font-bold mb-4">Wait! 🎯 Special One-Time Offer!</h2>
          <p className="text-xl opacity-90">Add a Personalized Strategy Call for Just ₹299</p>
        </div>

        <div className="p-8">
          <div className="text-center mb-8">
            <p className="text-lg text-gray-700 mb-4">
              You made a great decision! Now supercharge your results with personalized guidance.
            </p>
            
            <div className="bg-gradient-to-r from-yellow-50 to-orange-50 p-6 rounded-2xl border-2 border-orange-300">
              <h3 className="text-2xl font-bold text-gray-800 mb-4">
                1-to-1 Expert Call (30 Minutes) - ₹299
              </h3>
              
              <div className="space-y-4 text-left">
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiUser} className="w-5 h-5 text-orange-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-800">Personalized Strategy</h4>
                    <p className="text-gray-600">Get customized sales tactics for YOUR specific business</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiTarget} className="w-5 h-5 text-orange-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-800">Automation Setup</h4>
                    <p className="text-gray-600">Live demo of setting up WhatsApp & Email automations</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiZap} className="w-5 h-5 text-orange-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-800">Quick Wins</h4>
                    <p className="text-gray-600">Implementation of 3 strategies during the call</p>
                  </div>
                </div>
                
                <div className="flex items-start space-x-3">
                  <SafeIcon icon={FiCalendar} className="w-5 h-5 text-orange-500 mt-1 flex-shrink-0" />
                  <div>
                    <h4 className="font-semibold text-gray-800">Flexible Scheduling</h4>
                    <p className="text-gray-600">Book your call at your convenience (weekends available)</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-red-50 p-6 rounded-2xl mb-8">
            <div className="flex items-center justify-between mb-4">
              <span className="text-gray-500 line-through text-lg">Regular Price: ₹2,999</span>
              <div className="bg-red-500 text-white px-3 py-1 rounded-full text-sm font-semibold">
                90% OFF
              </div>
            </div>
            <div className="text-3xl font-bold text-red-600 text-center mb-4">
              Only ₹299 Today!
            </div>
            <p className="text-center text-sm text-gray-600">
              This is a ONE-TIME offer - you won't see this again!
            </p>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <button
              onClick={handleAcceptBump}
              disabled={isProcessing}
              className="flex-1 bg-gradient-to-r from-orange-500 to-red-500 text-white px-8 py-4 rounded-xl font-bold text-lg hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isProcessing ? (
                <span className="flex items-center justify-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </span>
              ) : (
                <span>🎯 Yes! Add Expert Call for ₹299</span>
              )}
            </button>
            
            <button
              onClick={onDecline}
              className="px-8 py-4 rounded-xl font-bold text-lg border-2 border-gray-300 text-gray-600 hover:bg-gray-50 transition-colors"
            >
              No thanks, continue with eBook only
            </button>
          </div>

          <div className="mt-6 text-center text-sm text-gray-500">
            <p>This offer will never be shown again after you skip.</p>
          </div>
        </div>
      </motion.div>
    </div>
  );
};

export default BumpOffer;